import React from 'react';
import { Download, Settings, Play, CheckCircle } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      icon: Download,
      title: 'Download & Install',
      description: 'Get the app from Google Play Store with just one tap. Installation takes seconds.',
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
    },
    {
      icon: Settings,
      title: 'Grant Permissions',
      description: 'Allow accessibility permissions so the touchpad can interact with your screen.',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      icon: Play,
      title: 'Activate Touchpad',
      description: 'Launch the app and turn on the touchpad. Customize position and size to your liking.',
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-100',
      iconColor: 'text-pink-600',
    },
    {
      icon: CheckCircle,
      title: 'Start Controlling',
      description: 'Use the virtual touchpad to navigate your entire screen with one hand effortlessly.',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
    },
  ];

  return (
    <section id="how-it-works" className="py-20 sm:py-28 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-10 w-72 h-72 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-10 left-1/2 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
            How It Works
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Get started in just 4 simple steps
          </p>
        </div>

        {/* Steps */}
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative group"
              >
                {/* Connector Line (hidden on mobile, shown on desktop between certain steps) */}
                {index < steps.length - 1 && index % 2 === 0 && (
                  <div className="hidden md:block absolute top-1/2 left-full w-12 h-0.5 bg-gradient-to-r from-indigo-300 to-purple-300 transform -translate-y-1/2 z-0"></div>
                )}

                {/* Card */}
                <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-gray-100 group-hover:border-indigo-200">
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg text-white font-bold text-xl">
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div className={`w-16 h-16 ${step.bgColor} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                    <step.icon className={`w-8 h-8 ${step.iconColor}`} />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Progress bar */}
                  <div className={`mt-6 w-0 group-hover:w-full h-1 bg-gradient-to-r ${step.color} transition-all duration-500 rounded-full`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-4">
            Ready to experience the convenience?
          </p>
          <div className="inline-flex items-center space-x-2 text-indigo-600 font-semibold text-lg">
            <span>Takes less than 2 minutes to setup</span>
            <CheckCircle className="w-5 h-5" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
